import { _js } from '@ifixit/utils/localize';
import { Alert, AlertDescription, AlertTitle } from '@ifixit/ui/components/alert';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { toast } from '@ifixit/ui/components/toast';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuLabel,
   DropdownMenuSeparator,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@ifixit/ui/components/sheet';
import { cn } from '@ifixit/ui/lib/utils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WriteStatusAnnouncer } from 'Shared/editor/action_bar';
import { PrimaryButton } from 'Shared/editor/buttons';
import { IconActionButton } from 'Shared/editor/IconActionButton';
import { SectionCard } from 'Shared/editor/section_card';
import {
   isLegacyModalOpen,
   isModalOpen,
   isModalPending,
   isShortcutClaimed,
} from 'Shared/editor/shortcut_scope';
import { StepEditor } from './StepEditor';
import { seedFromStep, type StepSeed } from './step_seed';
import type { ApiGuide, ApiStep, GuideVideoInfo } from './types';
import { movedItem, useDragReorder } from 'Shared/editor/use_drag_reorder';
import { useStepOrder } from './use_guide_edit_api';

type Selection = number | 'new' | null;

/**
 * The step selection encoded in the URL hash — #steps/123 or #steps/new —
 * so a selected step survives refresh and back/forward walks the steps
 * the user visited. Null when the hash names no step (another tab, or a
 * bare #steps).
 */
function selectionFromHash(): Selection {
   const [tab, step] = window.location.hash.slice(1).split('/');
   if (tab !== 'steps' || !step) {
      return null;
   }
   if (step === 'new') {
      return 'new';
   }
   const stepid = Number(step);
   return Number.isSafeInteger(stepid) && stepid > 0 ? stepid : null;
}

/**
 * Point the hash at a selection. Assigning location.hash pushes a history
 * entry (that's what makes Back work); `replace` rewrites the current
 * entry instead, for programmatic corrections — a created step replacing
 * #steps/new, a deletion, normalizing a bare #steps — that shouldn't
 * become Back stops.
 */
function writeSelectionHash(selection: Selection, options?: { replace: boolean }) {
   if (selection === null) {
      return;
   }
   const target = selection === 'new' ? 'steps/new' : `steps/${selection}`;
   if (window.location.hash.slice(1) === target) {
      return;
   }
   if (options?.replace) {
      const url = new URL(window.location.href);
      url.hash = target;
      window.history.replaceState(window.history.state, '', url);
      // replaceState doesn't fire hashchange; the tab hook reads the hash
      // through it, so poke it to re-read.
      window.dispatchEvent(new HashChangeEvent('hashchange'));
   } else {
      window.location.hash = target;
   }
}

/**
 * A modal painting above z-dialog (50) would hide the discard prompt
 * (#64245); narrower than isModalOpen(), which also matches the rail sheet.
 * A pending one counts too, or a switch during its code-split load opens the
 * prompt and the modal then mounts on top of it.
 */
function isDiscardPromptCovered(): boolean {
   if (isModalPending() || isLegacyModalOpen()) {
      return true;
   }
   return [...document.querySelectorAll('[data-slot="dialog-content"][data-state="open"]')].some(
      dialog => Number(getComputedStyle(dialog).zIndex) > 50
   );
}

type Drag = ReturnType<typeof useDragReorder>;

// The step editor hosts Tiptap toolbars and the whole line list; without
// memo every rail interaction (drag preview, selection ring) re-rendered it.
const MemoStepEditor = memo(StepEditor);

const noopCreated = () => {};

// Whatever the editor page keeps under the columns: the footer, its margin,
// and the page's own bottom padding. Measured, so a wrapping footer counts.
function spaceBelow(columns: HTMLElement): number {
   const page = columns.closest('[data-editor-page]');
   return page ? page.getBoundingClientRect().bottom - columns.getBoundingClientRect().bottom : 16;
}

function stepThumb(step: ApiStep): string | null {
   if (step.media.type !== 'image' || step.media.data.length === 0) {
      return null;
   }
   const image = step.media.data[0];
   return image.thumbnail ?? image.standard ?? image.original ?? null;
}

/**
 * The large rendition the editor's preview shows for a step's primary image
 * (mirrors StepMediaSelector's previewUrl precedence), or null when the step
 * has no image. This is what a step switch has to download.
 */
function stepPreviewUrl(step: ApiStep): string | null {
   if (step.media.type !== 'image' || step.media.data.length === 0) {
      return null;
   }
   const image = step.media.data[0];
   return image.large ?? image.medium ?? image.standard ?? image.original ?? null;
}

/** How many steps on each side of the selected one to warm. */
const IMAGE_PREFETCH_RADIUS = 2;

const NO_STEPS: ReadonlySet<number> = new Set();

/**
 * Warm the browser image cache for the steps on either side of the selected
 * one, so clicking through the rail shows the next step's preview without a
 * download wait. The rendition URLs already came down with the guide GET, so
 * this only primes the HTTP cache — no API calls. Windowed to ±radius so a
 * 100-step guide doesn't pull every large rendition (the rail already loads
 * the small thumbnails), and run at idle so it never competes with the
 * current step's own render.
 */
function usePrefetchNeighborImages(steps: ApiStep[], selectedIndex: number) {
   const warmedRef = useRef<Set<string>>(new Set());
   useEffect(() => {
      if (selectedIndex < 0) {
         return;
      }
      const urls: string[] = [];
      for (let offset = -IMAGE_PREFETCH_RADIUS; offset <= IMAGE_PREFETCH_RADIUS; offset++) {
         if (offset === 0) {
            continue; // The selected step loads through its own editor.
         }
         const neighbor = steps[selectedIndex + offset];
         const url = neighbor ? stepPreviewUrl(neighbor) : null;
         if (url !== null && !warmedRef.current.has(url)) {
            urls.push(url);
         }
      }
      if (urls.length === 0) {
         return;
      }
      const warm = () => {
         for (const url of urls) {
            warmedRef.current.add(url);
            new Image().src = url;
         }
      };
      if (typeof window.requestIdleCallback === 'function') {
         const handle = window.requestIdleCallback(warm);
         return () => window.cancelIdleCallback(handle);
      }
      const handle = window.setTimeout(warm, 200);
      return () => window.clearTimeout(handle);
   }, [steps, selectedIndex]);
}

/**
 * The Steps tab: an orderable step rail and the editor for the selected
 * step. Steps save individually (each has its own revisionid), so switching
 * steps warns when the current one has unsaved changes.
 *
 * memo: every keystroke in the Introduction/Details forms re-renders the
 * page, and forceMount keeps this tab mounted; its props only change when
 * the guide itself does.
 */
export const StepsTab = memo(function StepsTab({
   guideid,
   guide,
   initialStepid,
   initialNewStepOrderby,
   isActive,
   video,
   onDirtyChange,
   onOpenShortcuts,
   historyStepUrlBase,
}: {
   guideid: number;
   guide: ApiGuide;
   initialStepid: number | null;
   /** Insert position from a new-after step URL; null everywhere else. */
   initialNewStepOrderby: number | null;
   isActive: boolean;
   video: GuideVideoInfo | null;
   onDirtyChange: (dirty: boolean) => void;
   /** Opens the page-level keyboard-shortcuts dialog (Shift+/). */
   onOpenShortcuts: () => void;
   /**
    * Step-history URL prefix (append a stepid); null when the viewer
    * can't see history. Backs the rail rows' history menu item (#64082).
    */
   historyStepUrlBase: string | null;
}) {
   const [selected, setSelected] = useState<Selection>(() => {
      // The hash wins over the server-parsed URL: it's what a refresh (or
      // a reopened tab) carries after in-page navigation.
      const fromHash = selectionFromHash();
      if (fromHash === 'new') {
         return 'new';
      }
      if (typeof fromHash === 'number' && guide.steps.some(step => step.stepid === fromHash)) {
         return fromHash;
      }
      if (initialNewStepOrderby !== null) {
         return 'new';
      }
      if (initialStepid && guide.steps.some(step => step.stepid === initialStepid)) {
         return initialStepid;
      }
      return guide.steps[0]?.stepid ?? 'new';
   });
   // Where a created step lands: the new-after URL's position on first use,
   // append-at-end afterwards. Clamped to a valid insert position.
   const [newStepOrderby, setNewStepOrderby] = useState<number | null>(
      initialNewStepOrderby === null
         ? null
         : Math.min(Math.max(initialNewStepOrderby, 1), guide.steps.length + 1)
   );
   const stepOrder = useStepOrder(guideid);
   const stepDirtyRef = useRef(false);
   // A step the user tried to switch to while the current one had unsaved
   // changes; non-null renders the discard confirmation dialog.
   const [pendingSelection, setPendingSelectionState] = useState<Selection>(null);
   // The mobile "Rearrange steps" disclosure; closed on load so the editor
   // leads, and closed again once a step is picked from it.
   const [railOpen, setRailOpen] = useState(false);
   // Live editor state carried from a just-created step so edits made while
   // the create POST was in flight survive the remount onto the new stepid.
   const [createdSeed, setCreatedSeed] = useState<{ stepid: number; seed: StepSeed } | null>(null);
   // A duplicate's prefill for the 'new' editor. The nonce keys the editor
   // so duplicating remounts it onto the new seed even when the selection
   // is already 'new'.
   const [newStepSeed, setNewStepSeed] = useState<{ seed: StepSeed; nonce: number } | null>(null);
   const seedNonceRef = useRef(0);
   // What the next switch to the 'new' editor opens with (insert position
   // and optional duplicate prefill). Held in a ref and consumed only when
   // the selection actually lands on 'new' (through selectStep or the
   // discard dialog), so a rejected switch never clobbers a dirty editor.
   const stagedNewRef = useRef<{ orderby: number | null; seed: StepSeed | null }>({
      orderby: null,
      seed: null,
   });
   // Gates the page-level shortcuts to when this tab is actually shown
   // (forceMount keeps it in the DOM behind the other tabs).
   const rootRef = useRef<HTMLDivElement>(null);
   // The rail's own scroll container (md+): long guides scroll the step
   // list inside a viewport-height rail instead of stretching the page.
   const railScrollRef = useRef<HTMLDivElement>(null);

   // Keyed on isActive: a tab hidden by forceMount has no box to measure,
   // and no observer fires on reveal.
   useEffect(() => {
      const columns = rootRef.current;
      if (!columns || !isActive) {
         return;
      }
      let frame = 0;
      const update = () => {
         frame = 0;
         // A tab switch mid-frame leaves nothing to measure.
         if (columns.offsetParent === null) {
            return;
         }
         // Document offset, not viewport: rect.top in a scroll handler grew
         // the rail as the page scrolled, and the page with it.
         const documentTop = window.scrollY + columns.getBoundingClientRect().top;
         // Floor, so a short window still leaves a usable column to scroll to.
         const cap = Math.max(240, window.innerHeight - documentTop - spaceBelow(columns));
         columns.style.setProperty('--steps-cap', `${cap}px`);
      };
      const schedule = () => {
         frame ||= requestAnimationFrame(update);
      };
      update();
      window.addEventListener('resize', schedule);
      // Catches what moves the rail while the tab stays open, like a banner
      // mounting above it.
      const observer = new ResizeObserver(schedule);
      observer.observe(document.body);
      return () => {
         cancelAnimationFrame(frame);
         window.removeEventListener('resize', schedule);
         observer.disconnect();
      };
   }, [isActive]);

   // Keep the selection visible in the scrolling rail — a deep link lands
   // on it, and the Shift+;/H step walk can otherwise wander offscreen.
   // 'new' scrolls to the draft row so an insert position from a new-after
   // URL is on screen.
   useEffect(() => {
      if (typeof selected !== 'number' && selected !== 'new') {
         return;
      }
      railScrollRef.current
         ?.querySelector(`[data-railstep="${selected}"]`)
         ?.scrollIntoView({ block: 'nearest' });
   }, [selected]);

   // Reordering stages locally and applies in one PUT when "Save step
   // order" is clicked — live-saving every move was slow, and on phones a
   // mid-drag save felt like the list glitching.
   const [pendingOrder, setPendingOrder] = useState<number[] | null>(null);
   // Steps the user dragged. Which of two swapped rows moved is a fact only
   // the drag knows: both orders explain the change equally well.
   const [pickedUpIds, setPickedUpIds] = useState<ReadonlySet<number>>(NO_STEPS);

   // The rail's display order: the staged order reconciled against the
   // server's current steps (a step added or deleted while an order is
   // staged drops out of / appends to the staging instead of breaking it).
   const orderedSteps = useMemo(() => {
      if (pendingOrder === null) {
         return guide.steps;
      }
      const byId = new Map(guide.steps.map(step => [step.stepid, step]));
      const staged = pendingOrder.flatMap(stepid => byId.get(stepid) ?? []);
      const stagedIds = new Set(pendingOrder);
      return [...staged, ...guide.steps.filter(step => !stagedIds.has(step.stepid))];
   }, [guide.steps, pendingOrder]);

   const orderedIds = orderedSteps.map(step => step.stepid);
   const orderDirty =
      pendingOrder !== null &&
      orderedIds.join(',') !== guide.steps.map(step => step.stepid).join(',');
   // Which rows read as parked until the order saves: picked up by the user
   // (a swap's other row shifted, it didn't move) and still out of place.
   const movedStepIds = useMemo(() => {
      if (pickedUpIds.size === 0) {
         return NO_STEPS;
      }
      const savedIndex = new Map(guide.steps.map((step, index) => [step.stepid, index]));
      return new Set(
         orderedSteps.flatMap((step, index) =>
            pickedUpIds.has(step.stepid) && savedIndex.get(step.stepid) !== index
               ? [step.stepid]
               : []
         )
      );
   }, [pickedUpIds, guide.steps, orderedSteps]);

   // Render-time reset (the sanctioned derived-state adjustment): once the
   // server catches up with the staged order — the refetch after a save,
   // or the user moving things back — the staging is spent.
   if (pendingOrder !== null && !orderDirty) {
      setPendingOrder(null);
      setPickedUpIds(NO_STEPS);
   }

   // While the new-step editor is open, the unsaved step rides in the rail
   // as a draft row at its insert position: it takes a real step number
   // (the rows below shift by one) and drags like any other row. Clamped:
   // a deletion can shrink the list under a staged position.
   const draftIndex =
      selected === 'new'
         ? Math.min((newStepOrderby ?? orderedSteps.length + 1) - 1, orderedSteps.length)
         : null;
   const railEntries: (ApiStep | 'draft')[] = useMemo(() => {
      if (draftIndex === null) {
         return orderedSteps;
      }
      const entries: (ApiStep | 'draft')[] = [...orderedSteps];
      entries.splice(draftIndex, 0, 'draft');
      return entries;
   }, [orderedSteps, draftIndex]);

   // The discard prompt numbers its step the way the rail labels it, through
   // the staged order; counting guide.steps lies once a reorder is staged.
   const selectedRailNumber =
      railEntries.findIndex(entry => entry !== 'draft' && entry.stepid === selected) + 1;

   // The banner names rows by the number on their row label, so it counts
   // through railEntries: an open draft row shifts the numbers below it.
   const movedStepNumbers = useMemo(
      () =>
         railEntries.flatMap((entry, index) =>
            entry !== 'draft' && movedStepIds.has(entry.stepid) ? [index + 1] : []
         ),
      [railEntries, movedStepIds]
   );

   // Refs mirror the values the stable callbacks below read, so the
   // memoized rail rows and step editor don't re-render just because a
   // callback identity changed.
   const selectedRef = useRef(selected);
   const orderDirtyRef = useRef(orderDirty);
   const onDirtyChangeRef = useRef(onDirtyChange);
   const newStepOrderbyRef = useRef(newStepOrderby);
   const orderedStepsRef = useRef(orderedSteps);
   // An unsaved step order counts as unsaved changes too (it arms the
   // page's leave guard); staging changes without a step edit still needs
   // to report, hence the every-render sync.
   useEffect(() => {
      selectedRef.current = selected;
      orderDirtyRef.current = orderDirty;
      onDirtyChangeRef.current = onDirtyChange;
      newStepOrderbyRef.current = newStepOrderby;
      orderedStepsRef.current = orderedSteps;
      onDirtyChange(stepDirtyRef.current || orderDirty);
   });

   // Mirrored at the setter, not in the sync effect above: the step editor
   // reports clean from a child effect, which runs before that one.
   const pendingSelectionRef = useRef(pendingSelection);
   const setPendingSelection = useCallback((target: Selection) => {
      pendingSelectionRef.current = target;
      setPendingSelectionState(target);
   }, []);

   // Distinguishes the discard close from a "Keep editing" close in
   // onOpenChange, which runs after discardAndSelect with stale state
   // either way.
   const discardingRef = useRef(false);

   /**
    * Close the prompt the way "Keep editing" does: drop the rejected
    * switch's staged intent, point a back/forward URL back at the kept step.
    */
   const abandonPendingSelection = useCallback(() => {
      setPendingSelection(null);
      stagedNewRef.current = { orderby: null, seed: null };
      // Guarded like the correcting write below: Back can leave the steps
      // tab with the prompt still up.
      if (window.location.hash.slice(1).split('/')[0] === 'steps') {
         writeSelectionHash(selectedRef.current, { replace: true });
      }
   }, [setPendingSelection]);

   const setStepDirty = useCallback(
      (dirty: boolean) => {
         stepDirtyRef.current = dirty;
         onDirtyChangeRef.current(dirty || orderDirtyRef.current);
         // A parent-driven close never reaches onOpenChange, so abandon
         // here; mid-discard the intent is being applied, not abandoned.
         if (!dirty && pendingSelectionRef.current !== null && !discardingRef.current) {
            abandonPendingSelection();
         }
      },
      [abandonPendingSelection]
   );

   /**
    * Consume the staged insert position and duplicate seed into state, as
    * the selection lands on 'new'. Consuming (not just reading) means a
    * later plain switch to 'new' — the footer button, the hash — opens the
    * usual empty append editor instead of replaying a stale intent.
    */
   const applyStagedNew = useCallback(() => {
      const staged = stagedNewRef.current;
      stagedNewRef.current = { orderby: null, seed: null };
      // No upper clamp needed: the draft-row math caps the rail position at
      // the end of the list when steps were deleted underneath the intent.
      setNewStepOrderby(staged.orderby);
      seedNonceRef.current += 1;
      setNewStepSeed(staged.seed ? { seed: staged.seed, nonce: seedNonceRef.current } : null);
   }, []);

   // Refuse, don't queue: the prompt would open under the covering modal where nobody can see it.
   // The toast clears the modal (z-toast sits above every layer, the legacy .modalBox included).
   const refuseCoveredSwitch = useCallback(() => {
      abandonPendingSelection();
      // Generic wording: the media manager, photo lightbox and crop/markers dialogs all match.
      toast.error(_js('Close the open dialog before switching steps.'), { duration: 5000 });
   }, [abandonPendingSelection]);

   const selectStep = useCallback(
      (target: Selection) => {
         if (target === selectedRef.current) {
            return;
         }
         if (stepDirtyRef.current) {
            if (isDiscardPromptCovered()) {
               refuseCoveredSwitch();
               return;
            }
            setPendingSelection(target);
            // The prompt opens at z-dialog, under the phone rail sheet
            // (z-sheet); close the sheet so the prompt is seen.
            setRailOpen(false);
            // Back/forward already moved the URL to the rejected target;
            // point it back at the step still on screen.
            if (window.location.hash.slice(1).split('/')[0] === 'steps') {
               writeSelectionHash(selectedRef.current, { replace: true });
            }
            return;
         }
         setStepDirty(false);
         setSelected(target);
         if (target === 'new') {
            applyStagedNew();
         }
         // A history entry per visited step, so Back retraces them. When
         // the selection came FROM the hash (back/forward), this no-ops.
         writeSelectionHash(target);
         setCreatedSeed(null);
         setRailOpen(false);
      },
      [applyStagedNew, refuseCoveredSwitch, setPendingSelection, setStepDirty]
   );

   /**
    * Route to the 'new' editor with an insert position and (for duplicates)
    * a prefill, through the same dirty guard selectStep applies. Covers the
    * one gap selectStep can't: restaging while 'new' is already selected,
    * where the seed nonce remounts the editor onto the new intent.
    */
   const stageNewStep = useCallback(
      (orderby: number | null, seed: StepSeed | null) => {
         stagedNewRef.current = { orderby, seed };
         if (selectedRef.current === 'new') {
            if (stepDirtyRef.current) {
               if (isDiscardPromptCovered()) {
                  refuseCoveredSwitch();
                  return;
               }
               setPendingSelection('new');
               setRailOpen(false);
               return;
            }
            applyStagedNew();
            setRailOpen(false);
            return;
         }
         selectStep('new');
      },
      [applyStagedNew, refuseCoveredSwitch, selectStep, setPendingSelection]
   );

   const insertStepRelative = useCallback(
      (stepid: number, where: 'above' | 'below') => {
         const index = orderedStepsRef.current.findIndex(step => step.stepid === stepid);
         if (index === -1) {
            return;
         }
         // Positions are relative to the saved steps (not the displayed
         // rows), so an already-open draft row can't shift the target.
         stageNewStep(index + (where === 'above' ? 1 : 2), null);
      },
      [stageNewStep]
   );

   const duplicateStep = useCallback(
      (stepid: number) => {
         const steps = orderedStepsRef.current;
         const index = steps.findIndex(step => step.stepid === stepid);
         if (index === -1) {
            return;
         }
         // Just below the source; the author reviews and creates the copy.
         stageNewStep(index + 2, seedFromStep(steps[index]));
      },
      [stageNewStep]
   );

   const discardAndSelect = () => {
      // A second activation (double-click, or a click mid exit animation) would
      // select null and latch discardingRef; the ref dodges a stale closure.
      const target = pendingSelectionRef.current;
      if (target === null) {
         return;
      }
      discardingRef.current = true;
      setStepDirty(false);
      setSelected(target);
      if (target === 'new') {
         applyStagedNew();
      }
      writeSelectionHash(target);
      setPendingSelection(null);
      setCreatedSeed(null);
      setRailOpen(false);
   };

   // Programmatic selection changes (a create replacing #steps/new, a
   // delete, a refetch dropping the selected step) rewrite the current
   // entry — they correct a URL that now names a dead state, so they must
   // not become Back stops. User navigation already wrote the hash and
   // no-ops here. Guarded to the steps tab: this tab stays mounted (and
   // its selection state live) while the hash points at another tab.
   //
   // Declared BEFORE the hash-apply effect below, so its synthetic
   // hashchange finds the corrected hash or no listener, never a stale one.
   useEffect(() => {
      if (window.location.hash.slice(1).split('/')[0] === 'steps') {
         writeSelectionHash(selected, { replace: true });
      }
   }, [selected]);

   // Back/forward (and hand-edited URLs): apply the hash's step through
   // selectStep so the dirty-step guard still gets its say. A bare #steps
   // (the tab strip writes that) is normalized to the current selection so
   // the URL always names the step on screen.
   //
   // hashchange only, no eager run: that replayed the hash per refetch,
   // re-raising a rejected switch's prompt (#64245); mount reads the hash.
   useEffect(() => {
      const applyHash = () => {
         const target = selectionFromHash();
         if (target === null) {
            if (window.location.hash.slice(1) === 'steps') {
               writeSelectionHash(selectedRef.current, { replace: true });
            }
            return;
         }
         if (typeof target === 'number' && guide.steps.every(step => step.stepid !== target)) {
            return;
         }
         selectStep(target);
      };
      window.addEventListener('hashchange', applyHash);
      return () => window.removeEventListener('hashchange', applyHash);
   }, [guide.steps, selectStep]);

   // Rail reordering is drag and drop only (mouse and touch). A commit
   // reconstructs the previewed order of the combined list, then splits
   // it: the draft row's slot updates the create position, the saved
   // steps' order stages as a pending reorder (a draft-only move stages
   // the unchanged order, which the render-time reset above clears).
   const drag = useDragReorder((from, to) => {
      const next = movedItem(railEntries, from, to);
      const draftAt = next.indexOf('draft');
      if (draftAt !== -1) {
         setNewStepOrderby(draftAt + 1);
      }
      const picked = railEntries[from];
      if (picked !== 'draft') {
         setPickedUpIds(previous => new Set(previous).add(picked.stepid));
      }
      setPendingOrder(next.flatMap(entry => (entry === 'draft' ? [] : [entry.stepid])));
   });

   const discardOrder = () => {
      setPendingOrder(null);
      setPickedUpIds(NO_STEPS);
   };

   const saveOrder = () => {
      stepOrder.mutate(
         { revisionid: guide.revisionid, stepids: orderedIds },
         {
            // The button disappearing is the only other signal; make the
            // outcome legible mid-batch like the guide-level Save toast.
            onSuccess: () => {
               toast.success(_js('Step order saved.'), { duration: 4000 });
            },
            onError: () => {
               toast.error(
                  _js('The step order did not save — the guide changed underneath. Try again.'),
                  { duration: 5000 }
               );
            },
         }
      );
   };

   const selectedStep =
      typeof selected === 'number'
         ? (guide.steps.find(step => step.stepid === selected) ?? null)
         : null;

   // The selected step vanished from a refetch (another session deleted it)
   // while steps remain: reconcile to the first step so the editor pane
   // doesn't fall through to the "no steps yet" empty state.
   if (typeof selected === 'number' && selectedStep === null && guide.steps.length > 0) {
      setSelected(guide.steps[0].stepid);
   }

   // Prime the neighboring steps' preview images so rail click-through is
   // instant. -1 (a 'new'/null selection) warms nothing.
   const selectedIndex =
      typeof selected === 'number' ? guide.steps.findIndex(step => step.stepid === selected) : -1;
   usePrefetchNeighborImages(guide.steps, selectedIndex);

   // Step-level shortcuts: ↑/↓ walk the steps, Shift+/ opens the help,
   // Ctrl+Shift+Enter adds a step. Shift+; and Shift+H are undocumented
   // legacy synonyms for the arrows. Plain characters and bare arrows only
   // apply outside text inputs, and none of them apply while a modal (the
   // media manager and its dialogs) is open; the line-level Ctrl shortcuts
   // live in the step editor.
   useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
         // The step rail's grips consume arrow keys to reorder steps.
         if (event.defaultPrevented) {
            return;
         }
         // Add a step, even while a line editor has focus (the line editor
         // leaves Ctrl+Shift+Enter unconsumed so it bubbles here). Gated to
         // the Steps tab being on screen; selectStep no-ops if already on
         // the new-step row.
         if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Enter') {
            if (!isModalOpen() && rootRef.current && rootRef.current.offsetParent !== null) {
               selectStep('new');
               event.preventDefault();
            }
            return;
         }
         if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
         }
         const isArrow = !event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp');
         if (!isArrow && !event.shiftKey) {
            return;
         }
         if (isShortcutClaimed(event)) {
            return;
         }
         if (!rootRef.current || rootRef.current.offsetParent === null) {
            return;
         }
         if (event.key === '?') {
            onOpenShortcuts();
            event.preventDefault();
            return;
         }
         const isNext = (isArrow && event.key === 'ArrowDown') || event.key === ':';
         const isPrevious =
            (isArrow && event.key === 'ArrowUp') ||
            (event.shiftKey && event.key.toLowerCase() === 'h');
         if (!isNext && !isPrevious) {
            return;
         }
         // Walk the displayed order (which includes a staged, unsaved
         // reorder), not the server order.
         const index = orderedSteps.findIndex(step => step.stepid === selected);
         if (index === -1) {
            return;
         }
         const neighbor = orderedSteps[isNext ? index + 1 : index - 1];
         if (neighbor) {
            selectStep(neighbor.stepid);
         }
         event.preventDefault();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   });

   const handleDeleted = useCallback(() => {
      setStepDirty(false);
      const remaining = guide.steps.find(step => step.stepid !== selectedRef.current);
      setSelected(remaining?.stepid ?? 'new');
   }, [guide.steps, setStepDirty]);

   const handleCreated = useCallback(
      (stepid: number, seed?: StepSeed) => {
         setStepDirty(false);
         // A staged order predates the new stepid; without this splice the
         // reconciliation appends the created step at the end of the staged
         // list, teleporting it away from where the draft row sat.
         const at = newStepOrderbyRef.current;
         setPendingOrder(prev => {
            if (prev === null || prev.includes(stepid)) {
               return prev;
            }
            const next = [...prev];
            next.splice(Math.min((at ?? next.length + 1) - 1, next.length), 0, stepid);
            return next;
         });
         setNewStepOrderby(null);
         setNewStepSeed(null);
         stagedNewRef.current = { orderby: null, seed: null };
         setCreatedSeed(seed ? { stepid, seed } : null);
         setSelected(stepid);
      },
      [setStepDirty]
   );

   // Allocating 100+ option elements on every render added up; the list
   // only changes with the (staged) step order or the draft row. The draft
   // takes its number here too, so the phone picker agrees with the rail.
   // The same rail renders in the md+ sidebar column and inside the phone
   // sheet; the sheet's copy closes the sheet after acting.
   // Add a step keeps the full row until an unsaved order needs a home;
   // then the two share it. Kept outside the rail's scroll area so a long
   // guide never buries the buttons.
   return (
      <div
         ref={rootRef}
         // Phones stack the step picker over the editor; tighten that gap so
         // the first image sits higher in the viewport. md+ keeps gap-4 as
         // the rail/editor column gutter.
         // The rail alone sizes the row: per-step editor content must not,
         // or the bottom edge jumps. The literal holds the first frame.
         className="grid grid-cols-1 gap-2 md:grid-cols-[280px_1fr] md:gap-4 md:[--steps-cap:calc(100svh-2rem)]"
      >
         {/* At the root rather than beside the Save step order button: the
             rail renders twice, and the phone copy unmounts with its sheet —
             a live region has to already be in the tree when the write
             starts to announce it. */}
         <WriteStatusAnnouncer isSaving={stepOrder.isPending} />
         {/* The rail renders twice, a column on md+ and a "Rearrange steps"
             disclosure on phones; CSS swaps them so the first paint is right. */}
         <div
            // overflow-hidden rounds the list's corners off against the
            // card's, and the card's padding gives the rows' borders room.
            // 1000px was fitted to the GA4 screen heights: no laptop window
            // reaches it, monitors 1440 and taller stop here.
            className="hidden rounded-2xl border border-gray-300 bg-white md:flex md:max-h-[var(--steps-cap)] md:min-h-[min(var(--steps-cap),1000px)] md:flex-col md:overflow-hidden"
         >
            {movedStepNumbers.length > 0 && (
               // Pinned above the scroll area: the words stay put while the
               // dashed rows they explain scroll under them.
               <OrderBanner
                  movedNumbers={movedStepNumbers}
                  onUndo={discardOrder}
                  saving={stepOrder.isPending}
                  className="rounded-none border-b"
               />
            )}
            <div
               ref={railScrollRef}
               className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 scrollbar-minimal"
            >
               <RailList
                  drag={drag}
                  entries={railEntries}
                  selected={selected}
                  movedStepIds={movedStepIds}
                  hasVideo={video !== null}
                  onSelect={selectStep}
                  onInsert={insertStepRelative}
                  onDuplicate={duplicateStep}
                  historyUrlBase={historyStepUrlBase}
               />
            </div>
            <div className="border-t border-gray-200 p-3">
               <RailFooter
                  addDisabled={selected === 'new'}
                  orderDirty={orderDirty}
                  orderSaving={stepOrder.isPending}
                  movedCount={movedStepNumbers.length}
                  onAdd={() => stageNewStep(null, null)}
                  onSaveOrder={saveOrder}
               />
            </div>
         </div>

         {/* Phones lead with the editor; the row below names the selected
             step and opens the rail in a sheet — the same list, selection,
             and drag-reorder as the md+ sidebar. */}
         <div className="px-4 md:hidden">
            <button
               type="button"
               onClick={() => setRailOpen(true)}
               aria-label={_js('Choose a step to edit')}
               className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
               {selected === 'new' ? (
                  <>
                     <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-500">
                        <FaIcon name="plus" className="size-3.5" aria-hidden />
                     </span>
                     <span className="truncate font-medium">{_js('New step')}</span>
                  </>
               ) : selectedStep ? (
                  <>
                     {stepThumb(selectedStep) ? (
                        <img
                           src={stepThumb(selectedStep) ?? undefined}
                           alt=""
                           className="h-7 w-9 shrink-0 rounded border border-solid border-gray-300 object-cover"
                        />
                     ) : (
                        <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
                           <FaIcon name="image" className="size-3.5" aria-hidden />
                        </span>
                     )}
                     <span className="min-w-0 truncate">
                        <span className="font-medium">
                           {_js(
                              'Step %1',
                              String(
                                 guide.steps.findIndex(
                                    step => step.stepid === selectedStep.stepid
                                 ) + 1
                              )
                           )}
                        </span>
                        {selectedStep.title && <span>{_js(' — %1', selectedStep.title)}</span>}
                     </span>
                  </>
               ) : (
                  <span className="font-medium">{_js('Steps')}</span>
               )}
               <FaIcon
                  name="list-ul"
                  className="ml-auto size-4 shrink-0 text-gray-400"
                  aria-hidden
               />
            </button>
         </div>

         {/* The phone rail: the same list/footer as the sidebar, in a left
             sheet. Mounted only while open — rendering both copies doubled
             the work of every rail update. */}
         <Sheet open={railOpen} onOpenChange={setRailOpen}>
            <SheetContent side="left" className="w-80 max-w-[85vw] gap-0 p-4 md:hidden">
               <div className="mb-3 flex items-center justify-between">
                  <SheetTitle className="m-0 text-sm font-medium text-gray-950">
                     {_js('Steps')}
                  </SheetTitle>
                  <SheetClose
                     aria-label={_js('Close')}
                     className="flex size-7 items-center justify-center rounded text-gray-400 outline-none hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                     <FaIcon name="xmark" className="size-4" aria-hidden />
                  </SheetClose>
               </div>
               {railOpen && (
                  <>
                     {movedStepNumbers.length > 0 && (
                        <OrderBanner
                           movedNumbers={movedStepNumbers}
                           onUndo={discardOrder}
                           saving={stepOrder.isPending}
                           className="mb-3 border"
                        />
                     )}
                     <div className="-mx-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 py-0.5 scrollbar-minimal">
                        <RailList
                           drag={drag}
                           entries={railEntries}
                           selected={selected}
                           movedStepIds={movedStepIds}
                           hasVideo={video !== null}
                           onSelect={stepid => {
                              selectStep(stepid);
                              setRailOpen(false);
                           }}
                           onInsert={insertStepRelative}
                           onDuplicate={duplicateStep}
                           historyUrlBase={historyStepUrlBase}
                        />
                     </div>
                     {/* Same divider as the desktop rail card's footer; the
                         negative margins span it across the sheet's padding. */}
                     <div className="-mx-4 border-t border-gray-200 px-4 pt-3">
                        <RailFooter
                           addDisabled={selected === 'new'}
                           orderDirty={orderDirty}
                           orderSaving={stepOrder.isPending}
                           movedCount={movedStepNumbers.length}
                           onAdd={() => {
                              stageNewStep(null, null);
                              setRailOpen(false);
                           }}
                           onSaveOrder={saveOrder}
                        />
                     </div>
                  </>
               )}
            </SheetContent>
         </Sheet>

         {/* h-0 + min-h-full: out of the row's own sizing, then stretched
             back to exactly the row, so tall content scrolls inside. */}
         <SectionCard className="scrollbar-minimal md:h-0 md:min-h-full md:overflow-y-auto md:!pb-0">
            {selected === 'new' ? (
               <MemoStepEditor
                  // The nonce remounts the editor when a duplicate restages
                  // it while 'new' is already selected.
                  key={newStepSeed ? `new-step-${newStepSeed.nonce}` : 'new-step'}
                  guideid={guideid}
                  step={null}
                  seed={newStepSeed?.seed}
                  orderby={(draftIndex ?? guide.steps.length) + 1}
                  video={video}
                  onDirtyChange={setStepDirty}
                  onCreated={handleCreated}
                  onDeleted={handleDeleted}
               />
            ) : selectedStep ? (
               <MemoStepEditor
                  // Keyed by stepid only: the editor owns its post-save
                  // reseed, and keying on revisionid remounted it whenever
                  // a refetch landed, discarding anything typed in that
                  // window. External edits surface via the 409 conflict
                  // flow instead.
                  key={selectedStep.stepid}
                  guideid={guideid}
                  step={selectedStep}
                  seed={createdSeed?.stepid === selectedStep.stepid ? createdSeed.seed : undefined}
                  orderby={selectedStep.orderby}
                  video={video}
                  onDirtyChange={setStepDirty}
                  onCreated={noopCreated}
                  onDeleted={handleDeleted}
               />
            ) : (
               <p className="text-sm text-gray-500">
                  {_js('This guide has no steps yet. Add the first one.')}
               </p>
            )}
         </SectionCard>

         <AlertDialog
            open={pendingSelection !== null}
            onOpenChange={open => {
               if (open) {
                  return;
               }
               if (discardingRef.current) {
                  // discardAndSelect already applied the switch.
                  discardingRef.current = false;
                  return;
               }
               abandonPendingSelection();
            }}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>{_js('Discard unsaved changes?')}</AlertDialogTitle>
                  <AlertDialogDescription>
                     {selected === 'new'
                        ? _js('The new step has unsaved changes. Switching steps discards them.')
                        : selectedRailNumber > 0
                          ? _js(
                               'Step %1 has unsaved changes. Switching steps discards them.',
                               String(selectedRailNumber)
                            )
                          : _js('This step has unsaved changes. Switching steps discards them.')}
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>{_js('Keep editing')}</AlertDialogCancel>
                  <AlertDialogAction onClick={discardAndSelect}>
                     {_js('Discard changes')}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
});

/**
 * The rail row for the step being composed in the new-step editor. It
 * previews where the step will land: it wears the insert position's
 * number, and dragging it moves the insert position rather than staging a
 * reorder. Dashed border because nothing exists server-side yet; no
 * onSelect because it's only rendered while it IS the selection.
 */
const DraftRailRow = memo(function DraftRailRow({
   displayIndex,
   isDragging,
   rowProps,
   handleProps,
}: {
   displayIndex: number;
   isDragging: boolean;
   rowProps: Drag['rowProps'];
   handleProps: Drag['handleProps'];
}) {
   return (
      <div
         data-railstep="new"
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            'group flex cursor-grab items-center gap-2 rounded-lg border border-dashed border-brand-500 bg-white p-2 ring-1 ring-brand-500 active:cursor-grabbing',
            isDragging && 'opacity-60 ring-2 ring-brand-300'
         )}
      >
         <span
            {...handleProps(displayIndex, {
               label: _js('Reorder the new step (use the arrow keys to move it)'),
            })}
            className="relative flex h-6 w-4 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 outline-none group-hover:text-gray-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing pointer-coarse:after:absolute pointer-coarse:after:-inset-x-3 pointer-coarse:after:-inset-y-2 pointer-coarse:after:content-['']"
         >
            <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
         </span>
         <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
            <span className="flex h-9 w-12 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
               <FaIcon name="plus" className="size-4" />
            </span>
            <span className="min-w-0">
               <span className="line-clamp-2 text-sm leading-tight [overflow-wrap:anywhere]">
                  <span className="font-medium text-gray-600">
                     {_js('%1 · ', String(displayIndex + 1))}
                  </span>
                  <span className="text-gray-600 italic">{_js('New step')}</span>
               </span>
            </span>
         </span>
      </div>
   );
});

/**
 * One rail entry. memo'd because the rail can hold 100+ steps and renders
 * on every drag preview update and selection change; the hook's stable
 * rowProps/handleProps let a drag re-render only the rows that moved.
 * Reorder by dragging the grip, or by focusing it and pressing the arrow
 * keys (Home/End jump to an end); there are no separate move buttons.
 */
/**
 * The step rail's list: the same markup in the md+ sidebar column and in the
 * phone sheet — only the select handler differs (the sheet's closes it).
 */
function RailList({
   drag,
   entries,
   selected,
   movedStepIds,
   hasVideo,
   onSelect,
   onInsert,
   onDuplicate,
   historyUrlBase,
}: {
   drag: Drag;
   entries: (ApiStep | 'draft')[];
   selected: Selection;
   /** Steps sitting in an unsaved position, drawn as temporary. */
   movedStepIds: ReadonlySet<number>;
   hasVideo: boolean;
   onSelect: (stepid: number) => void;
   onInsert: (stepid: number, where: 'above' | 'below') => void;
   onDuplicate: (stepid: number) => void;
   /** Step-history URL prefix; null hides the rows' history item. */
   historyUrlBase: string | null;
}) {
   return (
      <div className="flex flex-col gap-2">
         <span aria-live="polite" className="sr-only">
            {drag.announcement}
         </span>
         {drag
            .displayItems(entries)
            .map(({ item }, displayIndex) =>
               item === 'draft' ? (
                  <DraftRailRow
                     key="draft"
                     displayIndex={displayIndex}
                     isDragging={drag.draggingIndex === displayIndex}
                     rowProps={drag.rowProps}
                     handleProps={drag.handleProps}
                  />
               ) : (
                  <RailRow
                     key={item.stepid}
                     step={item}
                     displayIndex={displayIndex}
                     isSelected={item.stepid === selected}
                     isMoved={movedStepIds.has(item.stepid)}
                     isDragging={drag.draggingIndex === displayIndex}
                     hasVideo={hasVideo}
                     rowProps={drag.rowProps}
                     handleProps={drag.handleProps}
                     onSelect={onSelect}
                     onInsert={onInsert}
                     onDuplicate={onDuplicate}
                     historyUrlBase={historyUrlBase}
                  />
               )
            )}
      </div>
   );
}

/**
 * Says in words what the dashed rows only show; without it the
 * leave-the-page warning was the first place that spelled it out.
 */
function OrderBanner({
   movedNumbers,
   onUndo,
   saving,
   className,
}: {
   movedNumbers: number[];
   onUndo: () => void;
   /** The PUT can't be cancelled, so Undo locks while it's in flight. */
   saving: boolean;
   className?: string;
}) {
   const detail =
      movedNumbers.length === 1
         ? _js('Step %1 moved. Save to apply.', String(movedNumbers[0]))
         : movedNumbers.length === 2
           ? _js(
                'Steps %1 and %2 moved. Save to apply.',
                String(movedNumbers[0]),
                String(movedNumbers[1])
             )
           : _js('%1 steps moved. Save to apply.', String(movedNumbers.length));
   return (
      <Alert
         // Not the default role="alert": an assertive region would pre-empt
         // the rail's polite move announcement on every arrow key.
         role="status"
         className={cn(
            '[--alert:var(--color-orange-50)] [--alert-foreground:var(--color-yellow-800)] [--alert-icon:var(--color-yellow-600)]',
            className
         )}
      >
         <FaIcon name="circle-exclamation" />
         <div className="col-start-2 flex items-center justify-between gap-3">
            <AlertTitle className="min-w-0 font-semibold">{_js('Step order not saved')}</AlertTitle>
            {/* rotate-left, not the mockup's rotate-right: same glyph, but
                undo curls counterclockwise (rotate-right is FA's redo). */}
            <IconActionButton
               label={_js('Undo')}
               icon="rotate-left"
               onClick={onUndo}
               disabled={saving}
               // after: is unconditional, unlike the base's touch-only inset:
               // a small button needs the 40px hit box on mouse too.
               className="size-6 shrink-0 rounded-md border border-yellow-600 bg-white text-yellow-700 hover:bg-orange-50 disabled:opacity-25 disabled:hover:bg-white after:absolute after:-inset-2 after:content-['']"
               iconClassName="size-2.5"
            />
         </div>
         <AlertDescription className="text-yellow-700">{detail}</AlertDescription>
      </Alert>
   );
}

/**
 * Add a step keeps the full row until an unsaved order needs a home; then
 * the two share it, or stack full-width when they cannot both fit — the
 * labels are `whitespace-nowrap`, so a long translation would otherwise
 * push the pair past the rail. Kept outside the rail's scroll area so a
 * long guide never buries the buttons.
 */
function RailFooter({
   addDisabled,
   orderDirty,
   orderSaving,
   movedCount,
   onAdd,
   onSaveOrder,
}: {
   addDisabled: boolean;
   orderDirty: boolean;
   orderSaving: boolean;
   /** Rows sitting in an unsaved position, shown on the save button. */
   movedCount: number;
   onAdd: () => void;
   onSaveOrder: () => void;
}) {
   return (
      <div className="flex flex-wrap gap-2">
         <Button
            type="button"
            className={cn('button-md button-outline', 'flex-auto justify-center')}
            // Manual adds append; only URL and rail-menu intents insert.
            onClick={onAdd}
            disabled={addDisabled}
         >
            <FaIcon name="plus" className="mr-2 size-3.5" />
            {_js('Add a step')}
         </Button>
         {orderDirty && (
            <PrimaryButton
               className="flex-auto justify-center"
               isPending={orderSaving}
               onClick={onSaveOrder}
            >
               {orderSaving
                  ? _js('Saving...')
                  : movedCount > 0
                    ? _js('Save step order (%1)', String(movedCount))
                    : _js('Save step order')}
            </PrimaryButton>
         )}
      </div>
   );
}

const RailRow = memo(function RailRow({
   step,
   displayIndex,
   isSelected,
   isMoved,
   isDragging,
   hasVideo,
   rowProps,
   handleProps,
   onSelect,
   onInsert,
   onDuplicate,
   historyUrlBase,
}: {
   step: ApiStep;
   displayIndex: number;
   isSelected: boolean;
   /** Dragged to a position the server doesn't have yet. */
   isMoved: boolean;
   isDragging: boolean;
   /** The guide has a video, so a step's visual is its video segment and a
       missing step image isn't a gap. */
   hasVideo: boolean;
   rowProps: Drag['rowProps'];
   handleProps: Drag['handleProps'];
   onSelect: (stepid: number) => void;
   /** Open the new-step editor inserting next to this step. */
   onInsert: (stepid: number, where: 'above' | 'below') => void;
   /** Open the new-step editor prefilled with this step's content. */
   onDuplicate: (stepid: number) => void;
   /** Step-history URL prefix; null hides the history item. */
   historyUrlBase: string | null;
}) {
   const thumb = stepThumb(step);
   // A publish-readiness nudge (not an error): a step with no image or a
   // blank line. Derived from the step prop (and the guide's hasVideo) so the
   // memoized row re-renders only when those change.
   const attention = [
      !hasVideo && step.media.type === 'image' && step.media.data.length === 0
         ? _js('No image yet')
         : null,
      step.lines.some(line => line.text_raw.trim().length === 0) ? _js('Has an empty line') : null,
   ]
      .filter(reason => reason !== null)
      .reduce((list, reason) => (list === '' ? reason : _js('%1, %2', list, reason)), '');
   // Absolute badge, so a step flipping ready ⇄ unready never resizes the
   // row. amber-600 because 500 measures 2.1:1 on white, under 3:1.
   const thumbBox = (
      <span className="relative shrink-0">
         {thumb ? (
            <img
               src={thumb}
               alt=""
               className="h-9 w-12 rounded border border-solid border-gray-300 object-cover"
            />
         ) : (
            <span className="flex h-9 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
               <FaIcon name="image" className="size-4" />
            </span>
         )}
         {attention !== '' && (
            <span
               aria-hidden
               className="absolute -right-1 -top-1 size-2.5 rounded-full bg-amber-600 ring-2 ring-white"
            />
         )}
      </span>
   );
   return (
      <div
         data-railstep={step.stepid}
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            // Grab cursor across the whole row: a press anywhere reorders it
            // (the ⋮ menu overrides back to a pointer).
            'group flex cursor-grab items-center gap-2 rounded-lg border bg-white p-2 active:cursor-grabbing',
            isSelected ? 'border-brand-500 bg-brand-50' : 'border-gray-300',
            // No hover shift on a moved row: hover:border-gray-400 ties the
            // dash color's specificity and sits later in the sheet, so it wins.
            !isSelected && !isMoved && 'hover:border-gray-400',
            isMoved &&
               'not-has-[:focus-visible]:border-dashed not-has-[:focus-visible]:border-yellow-600',
            isDragging && 'opacity-60 ring-2 ring-brand-300'
         )}
      >
         {/* Dragging works anywhere on the row (see rowProps draggable); the
             grip stays as the touch and keyboard affordance — its
             touch-action:none is what lets a touch drag from it, while a touch
             on the row body still scrolls the rail. Focus it and use the arrow
             keys to reorder without a pointer. */}
         <span
            {...handleProps(displayIndex, {
               label: _js(
                  'Reorder Step %1 (use the arrow keys to move it)',
                  String(displayIndex + 1)
               ),
            })}
            className="relative flex h-6 w-4 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 outline-none group-hover:text-gray-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing pointer-coarse:after:absolute pointer-coarse:after:-inset-x-3 pointer-coarse:after:-inset-y-2 pointer-coarse:after:content-['']"
         >
            <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
         </span>
         {/* data-drag: this button is the row's body, so a press that turns
             into a drag reorders (a plain click still selects the step). */}
         <button
            type="button"
            data-drag
            className="flex min-w-0 flex-1 cursor-grab items-center gap-2 text-left active:cursor-grabbing"
            onClick={() => onSelect(step.stepid)}
         >
            {/* The thumbnail is the hover target, not the 10px dot. Touch and
                keyboard can't open a tooltip, so the ⋮ menu repeats it. */}
            {attention === '' ? (
               thumbBox
            ) : (
               <Tooltip>
                  <TooltipTrigger asChild>{thumbBox}</TooltipTrigger>
                  <TooltipContent>{attention}</TooltipContent>
               </Tooltip>
            )}
            <span className="min-w-0">
               {step.title ? (
                  // The number folds into the title line so the title gets
                  // the row's width; two clamped lines, never more, and
                  // leading-tight keeps even two of them inside the 36px
                  // thumbnail height so rows stay one size. The sr-only
                  // label keeps "Step N" leading the accessible name;
                  // overflow-wrap lets an unbroken token (a part number, a
                  // URL) wrap instead of clipping without an ellipsis.
                  <span className="line-clamp-2 text-sm leading-tight text-gray-900 [overflow-wrap:anywhere]">
                     <span className="sr-only">{_js('Step %1', String(displayIndex + 1))}</span>
                     <span aria-hidden className="font-medium text-gray-600">
                        {_js('%1 · ', String(displayIndex + 1))}
                     </span>
                     {step.title}
                  </span>
               ) : (
                  // Same clamp as the titled branch: unclamped, this wraps to
                  // one glyph per line at 200% text and the row hits 234px.
                  <span className="line-clamp-2 text-sm font-medium leading-tight text-gray-600 [overflow-wrap:anywhere]">
                     {_js('Step %1', String(displayIndex + 1))}
                  </span>
               )}
               {/* The dashed border says this to everyone else. */}
               {isMoved && <span className="sr-only">{_js('moved, not saved yet')}</span>}
               {/* The badge dot is aria-hidden; this trailing text is the
                   screen-reader half of it. */}
               {attention !== '' && <span className="sr-only">{attention}</span>}
            </span>
         </button>
         {/* Per-row actions: quiet until the row is hovered or focused, but
             always visible on touch, where there is no hover to reveal it. */}
         <DropdownMenu>
            <DropdownMenuTrigger
               aria-label={_js('Step %1 actions', String(displayIndex + 1))}
               className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-400 opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
            >
               <FaIcon name="ellipsis-vertical" className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
               {/* The badge's tooltip is hover-only, so this is the only way
                   touch and keyboard ever read the reason. */}
               {attention !== '' && (
                  <>
                     <DropdownMenuLabel className="text-xs font-normal text-amber-700">
                        {attention}
                     </DropdownMenuLabel>
                     <DropdownMenuSeparator />
                  </>
               )}
               <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'above')}>
                  {_js('Insert step above')}
               </DropdownMenuItem>
               <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'below')}>
                  {_js('Insert step below')}
               </DropdownMenuItem>
               <DropdownMenuItem onSelect={() => onDuplicate(step.stepid)}>
                  {_js('Duplicate step')}
               </DropdownMenuItem>
               {historyUrlBase !== null && (
                  <DropdownMenuItem asChild>
                     {/* Item styles land on the <a> via Slot, but they set
                         no text color, so the document's `a` rule would
                         paint this one blue among black items. */}
                     <a href={historyUrlBase + step.stepid} className="text-popover-foreground">
                        {_js('Step history')}
                     </a>
                  </DropdownMenuItem>
               )}
            </DropdownMenuContent>
         </DropdownMenu>
      </div>
   );
});
